import React, { useEffect, useId, useState } from 'react';
import { InfoCircle } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { fetchBillingUsage, periodKey, pricingFor, recentPeriods } from '../../api/billing';
import { MockBadge } from '../embed/EmbedShared';
import { formatMoney, formatPercent, formatPeriod } from './billingFormat';

// "Ez a hónap": what the month adds up to so far — the unique matchings
// against the included ones, the overage (up to the cap), the commission on
// matched purchases, the base fee (or the trial), and the estimated total.
// The month picker reads past periods from …/billing/usage?period=YYYY-MM.

/// "egyedi matching" with its definition on hover / focus / tap.
export function MatchingTerm({ plural = false }) {
  const { t } = useLocale();
  const id = useId();
  return (
    <span className="bill-term">
      <button type="button" className="bill-term__btn" aria-describedby={id}>
        {t(plural ? 'billing.terms' : 'billing.term')}
        <InfoCircle size={11} aria-hidden="true" />
      </button>
      <span role="tooltip" id={id} className="bill-term__tip">{t('billing.termTip')}</span>
    </span>
  );
}

/// Fills `{term}` / `{terms}` in a translated string with the tooltip term.
function withTerm(text) {
  // split() with a capture group keeps the matched names at the odd indexes.
  return text.split(/\{(terms?)\}/).map((part, i) => (
    i % 2 === 1
      ? <MatchingTerm key={i} plural={part === 'terms'} />
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));
}

export function PriceList({ currency }) {
  const { t, lang } = useLocale();
  const p = pricingFor(currency);
  const cur = String(currency || 'HUF').toUpperCase() === 'EUR' ? 'EUR' : 'HUF';
  const money = (v) => formatMoney(v, cur, lang);
  const items = [
    t('billing.price.base', { fee: money(p.baseFee) }),
    t('billing.price.included', { n: p.includedViews.toLocaleString(lang === 'hu' ? 'hu-HU' : 'en-GB') }),
    t('billing.price.overage', { price: money(p.perView), cap: money(p.overageCap) }),
    t('billing.price.commission', { rate: formatPercent(p.commissionRate, lang) }),
  ];
  return (
    <ul className="bill-prices" data-testid="price-list">
      {items.map((text) => <li key={text}>{withTerm(text)}</li>)}
    </ul>
  );
}

export default function UsageCard({ brandId, current, mock: parentMock }) {
  const { t, lang } = useLocale();
  const thisMonth = periodKey();
  const [period, setPeriod] = useState(thisMonth);
  const [state, setState] = useState(() => (current ? { data: current, mock: parentMock, loading: false, error: null } : { data: null, mock: false, loading: true, error: null }));

  useEffect(() => {
    if (period === thisMonth && current) {
      setState({ data: current, mock: parentMock, loading: false, error: null });
      return undefined;
    }
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchBillingUsage(brandId, period)
      .then((res) => alive && setState({ data: res.data, mock: res.mock, loading: false, error: null }))
      .catch((err) => alive && setState((s) => ({ ...s, loading: false, error: err })));
    return () => {
      alive = false;
    };
  }, [brandId, period, thisMonth, current, parentMock]);

  const u = state.data;
  const isCurrent = period === thisMonth;
  const cur = u?.currency || 'HUF';
  const money = (v) => formatMoney(v, cur, lang);
  const num = (v) => Number(v || 0).toLocaleString(lang === 'hu' ? 'hu-HU' : 'en-GB');
  const p = pricingFor(cur);
  const included = u?.includedViews || p.includedViews;
  const fill = u ? Math.min(1, (u.uniqueViews || 0) / included) : 0;
  const capped = u && u.overageCap != null && u.overageAmount >= u.overageCap && u.overageViews > 0;

  return (
    <section className="panel-card bill-usage" data-testid="usage-card">
      <div className="emb-head">
        <div className="emb-head__title">
          <h2 className="panel-card__title">{isCurrent ? t('billing.usage.title') : formatPeriod(period, lang)}</h2>
          <MockBadge show={state.mock} />
        </div>
        <label className="bill-period">
          <select className="emb-input" aria-label={t('billing.usage.period')} value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="usage-period">
            {recentPeriods(6).map((key) => (
              <option key={key} value={key}>{formatPeriod(key, lang)}</option>
            ))}
          </select>
        </label>
      </div>

      {state.error && <p className="bill-inline-error" role="alert">{t('billing.loadError')}</p>}
      {!u && state.loading && <div className="emb-empty">{t('shell.loading')}</div>}

      {u && (
        <div className={state.loading ? 'is-refreshing' : undefined}>
          <div className="bill-meter">
            <div className="bill-meter__row">
              <span className="bill-meter__value">
                <strong data-testid="usage-unique">{num(u.uniqueViews)}</strong>
                <span> / {num(included)} {withTerm(t('billing.usage.includedOf'))}</span>
              </span>
              {u.overageViews > 0 && (
                <span className="bill-meter__over">{t('billing.usage.over', { n: num(u.overageViews) })}</span>
              )}
            </div>
            <div
              className={`bill-meter__track${u.overageViews > 0 ? ' is-over' : ''}`}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={included}
              aria-valuenow={Math.min(u.uniqueViews || 0, included)}
              aria-label={t('billing.usage.meterLabel')}
            >
              <span className="bill-meter__fill" style={{ width: `${fill * 100}%` }} />
            </div>
          </div>

          <dl className="bill-lines">
            <div className="bill-line">
              <dt>{t('billing.usage.baseFee')}</dt>
              <dd>
                {u.trial ? (
                  <span className="bill-trial" data-testid="usage-trial">{t('billing.usage.trial')}</span>
                ) : (
                  money(u.baseFee)
                )}
              </dd>
            </div>
            <div className="bill-line">
              <dt>
                {t('billing.usage.overage')}
                <span className="bill-line__sub">
                  {u.overageViews > 0
                    ? t('billing.usage.overageCalc', { n: num(u.overageViews), price: money(p.perView), cap: money(u.overageCap) })
                    : t('billing.usage.overageNone', { cap: money(u.overageCap) })}
                  {capped && <span className="bill-cap"> · {t('billing.usage.capReached')}</span>}
                </span>
              </dt>
              <dd data-testid="usage-overage">{money(u.overageAmount)}</dd>
            </div>
            <div className="bill-line">
              <dt>
                {t('billing.usage.commission', { rate: formatPercent(u.commissionRate, lang) })}
                <span className="bill-line__sub">
                  {t('billing.usage.commissionCalc', { n: num(u.matchedPurchases), revenue: money(u.matchedRevenue) })}
                </span>
              </dt>
              <dd data-testid="usage-commission">{money(u.commissionAmount)}</dd>
            </div>
            <div className="bill-line bill-line--total">
              <dt>{t(isCurrent ? 'billing.usage.estimated' : 'billing.usage.total')}</dt>
              <dd data-testid="usage-total">{money(u.estimatedTotal)}</dd>
            </div>
          </dl>
        </div>
      )}

      <div className="bill-prices-wrap">
        <div className="bill-prices__title">{t('billing.price.title')}</div>
        <PriceList currency={cur} />
      </div>
    </section>
  );
}
